import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import BookCover from "@/components/BookCover";
import { Seo } from "@/components/Seo";

const CATS = [
  { id: "all", label: "All" },
  { id: "hardcover", label: "Hardcover" },
  { id: "paperback", label: "Paperback" },
  { id: "ebook", label: "eBook" },
  { id: "curriculum", label: "Curriculum" },
];

export default function Bookstore() {
  const [books, setBooks] = useState([]);
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = cat === "all" ? {} : { category: cat };
    api.get("/books", { params }).then((r) => setBooks(r.data)).finally(() => setLoading(false));
  }, [cat]);

  return (
    <div data-testid="bookstore-page">
      <Seo
        title="The Catalog — Books by 1123 Publishing | Bookstore"
        description="Browse the 1123 Publishing catalog: memoirs, social justice literature, and transformational curriculum in hardcover, paperback, and ebook editions."
        path="/bookstore"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "1123 Publishing Bookstore",
          url: "https://1123publishing.com/bookstore",
          mainEntity: {
            "@type": "ItemList",
            itemListElement: books.map((b, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://1123publishing.com/book/${b.slug}`,
              name: b.title,
            })),
          },
        }}
      />
      <section className="bg-navy text-ivory py-20 md:py-28 relative overflow-hidden">
        <div className="parchment-overlay" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 text-center">
          <div className="overline text-gold mb-3">The 1123 Bookstore</div>
          <h1 className="font-serif text-5xl md:text-6xl">The Catalog.</h1>
          <p className="mt-5 max-w-xl mx-auto text-ivory/80">
            Hardcover, paperback, eBook, and educational curriculum &mdash; printed and produced with care.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap gap-3 justify-center mb-14" data-testid="bookstore-categories">
            {CATS.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`category-${c.id}`}
                className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                  cat === c.id
                    ? "bg-navy text-ivory border-navy"
                    : "bg-transparent text-navy border-navy/30 hover:border-navy"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-24 text-navy/60">Loading the shelf&hellip;</div>
          ) : books.length === 0 ? (
            <div className="text-center py-24 text-navy/60">No titles in this category yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 md:gap-x-14 lg:gap-x-20 gap-y-20 md:gap-y-24 px-4 md:px-10">
              {books.map((b) => (
                <div key={b.id} className="space-y-3">
                  <BookCover book={b} />
                  <div className="text-center text-sm font-serif text-navy/80">
                    {b.coming_soon ? <span className="text-gold uppercase text-xs tracking-[0.22em]">Coming Soon</span> : `$${Number(b.price).toFixed(2)}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 md:py-28 bg-parchment border-t border-navy/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="overline mb-3">Stay In Touch</div>
          <h2 className="font-serif text-3xl md:text-4xl text-navy">New releases reach the reading room first.</h2>
          <p className="mt-4 text-charcoal/80 text-sm leading-relaxed">
            Join the 1123 reading room for new release announcements, author interviews,
            and the occasional letter from our editors.
          </p>
          <p className="mt-6 text-xs text-navy/55">
            Use the subscribe field in the footer below to join.
          </p>
        </div>
      </section>
    </div>
  );
}
